import { shell } from "electron";
import update from "immutability-helper";
import * as path from "path";
import * as React from "react";
import { Button, Collapse } from "react-bootstrap";
import { generate as shortid } from "shortid";
import { showDialog } from "../../../actions/notifications";
import { ComponentEx, connect, translate } from "../../../controls/ComponentEx";
import * as fs from "../../../util/fs";
import getVortexPath from "../../../util/getVortexPath";
import { log } from "../../../util/log";
import { activeGameId } from "../../../util/selectors";
import { getSafe } from "../../../util/storeHelper";
import MainPage from "../../../views/MainPage";
import { getGame } from "../../gamemode_management/util/getGame";
import { setFeature, setProfile } from "../actions/profiles";
import { setNextProfile } from "../actions/settings";
import { profilePath, removeProfile } from "../util/manage";
import ProfileEdit from "./ProfileEdit";
import ProfileItem from "./ProfileItem";
/**
 * presents profiles and allows creation of new ones
 *
 * @class ProfileView
 */
class ProfileView extends ComponentEx {
    constructor(props) {
        super(props);
        this.state = {
            edit: null,
            highlightGameId: undefined,
            showOther: true,
        };
    }
    render() {
        const { t, activity, features, gameId, language, profiles } = this.props;
        const { edit, showOther } = this.state;
        const currentGameProfiles = {};
        const otherProfiles = {};
        Object.keys(profiles).forEach((profileId) => {
            if (profiles[profileId].gameId === undefined ||
                profiles[profileId].name === undefined) {
                return;
            }
            if (profiles[profileId].gameId === gameId) {
                currentGameProfiles[profileId] = profiles[profileId];
            }
            else {
                otherProfiles[profileId] = profiles[profileId];
            }
        });
        const currentGameProfilesSorted = this.sortProfiles(currentGameProfiles, language);
        const otherProfilesSorted = this.sortProfiles(otherProfiles, language);
        const isDeploying = activity.includes("deployment") || activity.includes("purging");
        // const sortedProfiles: string[] = this.sortProfiles(profiles, language);
        const supportedFeatures = features.filter((feature) => feature.supported());
        return (React.createElement(MainPage, null,
            React.createElement(MainPage.Body, { style: { overflowY: "auto" } },
                React.createElement("div", { className: "profile-list" }, currentGameProfilesSorted.map((profileId) => this.renderProfile(profileId, supportedFeatures))),
                this.renderAddOrEdit(edit),
                !this.props.useModernLayout && (React.createElement(React.Fragment, null,
                    React.createElement("div", null,
                        t("Other Games"),
                        " ",
                        React.createElement("a", { onClick: this.toggleOther }, showOther ? t("Hide") : t("Show"))),
                    React.createElement(Collapse, { in: showOther },
                        React.createElement("div", null,
                            React.createElement("div", { className: "profile-list" }, otherProfilesSorted.map((profileId) => this.renderProfile(profileId, supportedFeatures))))))),
                isDeploying ? this.renderOverlay() : null)));
    }
    renderOverlay() {
        const { t } = this.props;
        return React.createElement("div", { className: "profile-overlay" }, t("Deployment in progress"));
    }
    sortProfiles(profiles, language) {
        return Object.keys(profiles).sort((lhs, rhs) => profiles[lhs].gameId !== profiles[rhs].gameId
            ? profiles[lhs].gameId.localeCompare(profiles[rhs].gameId)
            : profiles[lhs].name.localeCompare(profiles[rhs].name, language, {
                sensitivity: "base",
            }));
    }
    renderProfile = (profileId, features) => {
        const { t, mods } = this.props;
        const { edit } = this.state;
        if (profileId === edit) {
            return this.renderEditProfile();
        }
        const { currentProfile, discoveredGames, onSetNextProfile, profiles } = this.props;
        if (profiles[profileId] === undefined) {
            return null;
        }
        const discovered = discoveredGames[profiles[profileId].gameId];
        const available = discovered !== undefined && discovered.path !== undefined;
        const gameAvailable = getGame(profiles[profileId].gameId)?.name !== undefined;
        if (profileId === this.state.edit) {
            return null;
        }
        const profile = profiles[profileId];
        return (React.createElement(ProfileItem, { active: currentProfile === profileId, available: available && gameAvailable, features: features, highlightGameId: this.state.highlightGameId, key: profileId, mods: mods[profile.gameId] ?? emptyObject, profile: profile, t: t, onActivate: onSetNextProfile, onClone: this.onCloneProfile, onCreateShortcut: this.setShortcut, onRemove: this.onRemoveProfile, onSetHighlightGameId: this.setHighlightGameId, onStartEditing: this.editExistingProfile }));
    };
    toggleOther = () => {
        this.setState(update(this.state, {
            showOther: { $set: !this.state.showOther },
        }));
    };
    setHighlightGameId = (gameId) => {
        this.setState(update(this.state, {
            highlightGameId: { $set: gameId },
        }));
    };
    setShortcut = (profileId) => {
        const { t, profiles } = this.props;
        const profile = profiles[profileId];
        const exePath = getVortexPath("exe");
        const isDevelopment = exePath.toLowerCase().endsWith("electron.exe");
        const desktopLocation = getVortexPath("desktop");
        const shortcutPath = path.join(desktopLocation, `Start Vortex Profile_${profileId}(${profile.gameId}).lnk`);
        // In development, electron.exe needs the app directory as first argument
        const target = isDevelopment
            ? exePath
            : path.join(path.dirname(exePath), "Vortex.exe");
        const args = isDevelopment
            ? `"${getVortexPath("package")}" --profile ${profileId}`
            : `--profile ${profileId}`;
        const created = shell.writeShortcutLink(shortcutPath, "create", {
            target,
            args,
        });
        const displayMS = 5000;
        const message = created
            ? t("Vortex profile shortcut saved to desktop")
            : t("Failed to save profile shortcut to desktop");
        const type = created ? "info" : "error";
        this.context.api.sendNotification({ message, type, displayMS });
    };
    renderAddOrEdit(editId) {
        return editId === null
            ? this.renderAddProfile()
            : editId === "__new"
                ? this.renderEditProfile()
                : null;
    }
    renderEditProfile() {
        const { t, features, gameId, onSetFeature, profiles } = this.props;
        const { edit } = this.state;
        let profile;
        if (edit !== "__new") {
            profile = profiles[edit];
        }
        return (React.createElement(ProfileEdit, { features: features, gameId: gameId, key: edit, profile: profile, profileId: edit, t: t, onCancelEdit: this.endEdit, onSaveEdit: this.saveEdit, onSetFeature: onSetFeature }));
    }
    renderAddProfile() {
        const { t } = this.props;
        const { discoveredGames, gameId, games } = this.props;
        if (gameId === undefined) {
            return null;
        }
        const game = games.find((iter) => iter.id === gameId);
        const discovered = discoveredGames[gameId];
        let gameName = getSafe(discovered, ["name"], getSafe(game, ["name"], ""));
        if (gameName !== undefined) {
            gameName = gameName
                .split("\t")
                .map((part) => t(part))
                .join(" ");
        }
        return (React.createElement("div", { style: { display: "flex", justifyContent: "center" } },
            React.createElement(Button, { bsStyle: "ghost", className: "profile-add", onClick: this.editNewProfile }, t('Add "{{ name }}" Profile', { replace: { name: gameName } }))));
    }
    saveEdit = (profile) => {
        const { onAddProfile } = this.props;
        if (profile.id === "__new") {
            const newId = shortid();
            const newProf = update(profile, { id: { $set: newId } });
            fs.ensureDirAsync(profilePath(newProf)).then(() => {
                onAddProfile(newProf);
            });
        }
        else {
            onAddProfile(profile);
        }
        this.endEdit();
    };
    endEdit = () => {
        this.setState(update(this.state, {
            edit: { $set: null },
        }));
    };
    editNewProfile = () => {
        this.setState(update(this.state, {
            edit: { $set: "__new" },
        }));
        this.context.api.events.emit("analytics-track-click-event", "Profile", `Add new profile`);
    };
    onCloneProfile = (profileId) => {
        const { onAddProfile, profiles } = this.props;
        const newProfile = { ...profiles[profileId] };
        newProfile.id = shortid();
        fs.ensureDirAsync(profilePath(profiles[profileId]))
            .then(() => fs.copyAsync(profilePath(profiles[profileId]), profilePath(newProfile)))
            .then(() => {
            onAddProfile(newProfile);
            this.editExistingProfile(newProfile.id);
        })
            .catch((err) => this.context.api.showErrorNotification("Failed to clone profile", err, {
            allowReport: err.code !== "EPERM",
        }));
    };
    onRemoveProfile = (profileId) => {
        const { currentProfile, onShowDialog, profiles } = this.props;
        const gameMode = profiles[profileId].gameId;
        const totalProfilesForGame = gameMode
            ? Object.keys(profiles).filter((id) => profiles[id].gameId === gameMode)
                .length
            : 0;
        let confirmText = profileId === currentProfile
            ? 'You are trying to remove your currently active profile, "{{profileName}}". ' +
                "This will result in Vortex exiting to the dashboard screen, with no active profile set. " +
                "Remove this profile? Note: the removed profile cannot be restored!"
            : 'Remove the profile "{{profileName}}"? This can\'t be undone!';
        confirmText =
            totalProfilesForGame === 1
                ? confirmText +
                    " As this is your only profile for this game, removing it will unmanage the " +
                    "game within Vortex!"
                : confirmText;
        onShowDialog("question", "Confirm", {
            text: confirmText,
            parameters: { profileName: profiles[profileId].name },
        }, [
            { label: "Cancel", default: true },
            {
                label: "Remove",
                action: () => {
                    log("info", "user removing profile", { id: profileId });
                    removeProfile(this.context.api, profileId);
                },
            },
        ]);
    };
    editExistingProfile = (profileId) => {
        this.setState(update(this.state, {
            edit: { $set: profileId },
        }));
    };
}
const emptyArray = [];
const emptyObject = {};
function mapStateToProps(state) {
    const gameId = activeGameId(state);
    return {
        gameId,
        currentProfile: state.settings.profiles.activeProfileId,
        profiles: state.persistent.profiles,
        language: state.settings.interface.language,
        mods: state.persistent.mods || emptyObject,
        games: state.session.gameMode.known,
        discoveredGames: state.settings.gameMode.discovered,
        activity: getSafe(state, ["session", "base", "activity", "mods"], emptyArray),
        useModernLayout: state.settings.window.useModernLayout,
    };
}
function mapDispatchToProps(dispatch) {
    return {
        onAddProfile: (profile) => dispatch(setProfile(profile)),
        onSetNextProfile: (profileId) => dispatch(setNextProfile(profileId)),
        onSetFeature: (profileId, featureId, value) => dispatch(setFeature(profileId, featureId, value)),
        onShowDialog: (type, title, content, actions) => dispatch(showDialog(type, title, content, actions)),
    };
}
export default translate(["common"])(connect(mapStateToProps, mapDispatchToProps)(ProfileView));
